import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { ArchWindow } from "./ArchWindow";
import { MobileMenu } from "./MobileMenu";
import { usePastorAuth } from "../lib/pastorAuth";

const links = [
  { label: "Mission", href: "#manifesto" },
  { label: "Services", href: "#services" },
  { label: "The Work", href: "#portfolio" },
  { label: "Apply", href: "#apply" },
  { label: "Give", href: "#donate" },
  { label: "My Story", href: "#about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pastor } = usePastorAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${scrolled ? "glass" : "bg-transparent"}`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
          <ArchWindow size={26} />
          <div className="leading-none">
            <span className="font-orbitron font-black text-[#120716] text-lg tracking-widest">DIGITAL</span>
            <span className="block font-orbitron text-[10px] tracking-[0.5em] text-[#E84C4C]">MISSIONS</span>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#120716]/60 hover:text-[#120716] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          {pastor && (
            <Link to="/account" data-testid="nav-account-link" title="My applications" className="flex items-center gap-2 group">
              {pastor.picture ? (
                <img src={pastor.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full border-2 border-white shadow-sm group-hover:border-[#E84C4C] transition-colors" />
              ) : (
                <span className="w-9 h-9 rounded-full bg-[#E84C4C]/15 text-[#E84C4C] flex items-center justify-center font-bold text-sm">{(pastor.name || pastor.email)[0]?.toUpperCase()}</span>
              )}
              <span className="hidden lg:block text-xs font-semibold text-[#120716]/60 group-hover:text-[#E84C4C] transition-colors">My applications</span>
            </Link>
          )}
          <a
            href="#apply"
            data-testid="nav-apply-cta"
            className="rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] text-white text-sm font-semibold px-5 sm:px-6 py-2.5 transition-all duration-300 hover:shadow-[0_0_28px_rgba(232,76,76,0.5)]"
          >
            <span className="sm:hidden">Apply</span>
            <span className="hidden sm:inline">Apply for Help</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={menuOpen}
            className="md:hidden w-10 h-10 rounded-full border border-[#120716]/10 bg-white/70 flex items-center justify-center text-[#120716] hover:border-[#E84C4C] hover:text-[#E84C4C] transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.header>
    <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} links={pastor ? [...links, { label: "My Applications", href: "/account" }] : links} />
    </>
  );
};
